import { Component, HostBinding } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RenderCheckoutError, RenderJob, RenderNotImplementedError } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { RenderHelpService } from '../render-help.service';
import { RENDER_FULL_ETA, RENDER_OFFER_BENEFITS, RENDER_PRICE_USD, RENDER_START_CONSENT, RENDER_VIDEO_SPEC_PARTS } from '../render.data';
import { renderLegalUrl } from '../legal/render-legal.data';
import { RenderStepBase } from './render-step-base';

// Step 2: the free result — one frame, both orientations — and the order card (Figma
// 28516:21496). The prototype showed watermarked videos here; the product decision (owner,
// 2026-09-21) is a still, and the videos exist only after the purchase. "Try a different model"
// is the stepper's chevron now.
//
// Straight from processing (same layout, empty screens) the frames develop on the screens and
// the card's offer comes in after them; any later visit shows them outright.
@Component({
  selector: 'app-render-preview-step',
  templateUrl: './render-preview-step.component.html',
})
export class RenderPreviewStepComponent extends RenderStepBase {
  @HostBinding('class') public readonly hostClass = 'render-step step-preview';
  @HostBinding('class.is-revealing') public reveal = false;

  public readonly price = RENDER_PRICE_USD;
  public readonly videoSpec = RENDER_VIDEO_SPEC_PARTS;
  public readonly benefits = RENDER_OFFER_BENEFITS;
  public readonly fullEta = RENDER_FULL_ETA;
  public readonly startConsentText = RENDER_START_CONSENT;
  public readonly termsUrl = renderLegalUrl('terms');
  public readonly refundsUrl = renderLegalUrl('refunds');

  // The request to start at once: the checkout waits for it.
  public startConsent = false;
  public busy = false;
  public purchaseError = '';

  private revealChecked = false;
  // The model whose preview the product analytics have seen opened.
  private counted = '';

  constructor(route: ActivatedRoute, router: Router, flow: RenderFlowService, private readonly help: RenderHelpService) {
    super(route, router, flow);
  }

  protected override onJob(job: RenderJob): void {
    if (!this.revealChecked) {
      this.revealChecked = true;
      this.reveal = this.flow.takeReveal(job.modelId);
    }
    if (this.counted !== job.modelId && job.step === 'preview') {
      this.counted = job.modelId;
      // `fresh`: straight from processing, the frames developing; otherwise a later visit.
      this.flow.track('preview_viewed', job, { fresh: this.reveal, paid: job.entitlement === 'full' });
    }
  }

  // An upload whose preview is a sample's frames (uploads are not rendered in this build).
  public get standIn(): boolean {
    return this.isUpload && !!this.sample;
  }

  // An FBX that asks for no texture at all: the export guide's line under the frames. Not for USD,
  // nor an FBX that could not be read — both leave modelTextures out.
  public get noTextures(): boolean {
    const source = this.job?.source;
    return source?.kind === 'upload' && source.modelTextures === 0;
  }

  public get uploadFileName(): string {
    const source = this.job?.source;
    return source?.kind === 'upload' ? source.fileName : '';
  }

  // Both lines under the frames say how to include textures: the guide opens on the step that
  // packs them in.
  public openGuide(): void {
    const source = this.job?.source;
    this.help.open({ exporter: source?.kind === 'upload' ? source.exporter : undefined, step: 'pack' });
  }

  // A model already paid for: its videos need no checkout. A membership does not count (owner,
  // 2026-10-14: $9 for everyone).
  public get unlocked(): boolean {
    return this.job?.entitlement === 'full';
  }

  public async onPurchase(): Promise<void> {
    if (!this.job || this.busy || this.isSample || (!this.unlocked && !this.startConsent)) {
      return;
    }
    this.busy = true;
    this.purchaseError = '';
    let leaving = false;
    try {
      const next = await this.flow.beginPurchase(this.job.modelId);
      if (next.kind === 'redirect') {
        // Busy until the browser has left for Stripe: another click would open another checkout.
        leaving = true;
        window.location.href = next.url;
        return;
      }
      // A step change: the shell follows the job.
    } catch (error) {
      this.purchaseError = error instanceof RenderNotImplementedError || error instanceof RenderCheckoutError ? error.message : 'Checkout could not be started. Please try again.';
      console.error('Render flow: purchase failed', error);
    } finally {
      this.busy = leaving;
    }
  }
}
